// src/components/GroupDetailPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// Import API service
import { getGroupById, updateStudentEmailPreference, getScheduleEntries } from '../services/apiService';
// Import constants
import { TEACHER_PREFERENCE_STATUS } from '../constants/preferences';
import Card from './ui/Card';
import Button from './ui/Button';
import Input from './ui/Input';
import Modal from './ui/Modal';
import EditScheduleItemModal from './EditScheduleItemModal';
import TeacherPreferenceGrid from './TeacherPreferenceGrid';
import HelpIcon from './Documentation/HelpIcon';
import { parseSlotKey, formatTimeFromMinutes, timeToMinutes, getWeekNumber } from '../utils/helpers.ts';
import ScheduleEntry from '../models/ScheduleEntry';
import AcademicYearService from '../services/AcademicYearService.ts';
import AssociatedClassesCard from './AssociatedClassesCard';
import EntityTagsSection from './EntityTagsSection';

// Icons (keep as is)
const TrashIcon = () => (/* ... svg ... */
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);
const PencilIcon = () => (/* ... svg ... */
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);
const ImportIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
  </svg>
);
const MailIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);
const MailSlashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
  </svg>
);
const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

function GroupDetailPage({
  user,
  group,
  groups,
  teachers,
  subjects,
  rooms,
  onUpdateGroup,
  onDeleteGroup,
  onAddStudentToGroup,
  onAddStudentsToGroupBatch,
  onRemoveStudentFromGroup,
  onAddNewClass
}) {
  const { groupId: groupIdFromParams } = useParams();
  const navigate = useNavigate();
  const currentUrlGroupId = groupIdFromParams ? parseInt(groupIdFromParams) : null;

  // --- STATE INITIALIZATION REFINEMENT ---
  // Check if the incoming 'group' prop is valid for the current URL ID AT MOUNT TIME
  const isGroupPropInitiallyValid =
    group &&
    currentUrlGroupId !== null &&
    group.id === currentUrlGroupId &&
    group.students && // Heuristic for "complete" object
    group.preferences;

  const [internalGroupData, setInternalGroupData] = useState(
    isGroupPropInitiallyValid ? group : null
  );

  // isLoading is true ONLY if the prop is NOT initially valid AND we have a URL ID to fetch for.
  const [isLoading, setIsLoading] = useState(
    currentUrlGroupId !== null && !isGroupPropInitiallyValid
  );

  const [editedName, setEditedName] = useState(
    isGroupPropInitiallyValid ? (group.name || '') : ''
  );

  const [associatedClasses, setAssociatedClasses] = useState([]);
  const [hoveredCourseIds, setHoveredCourseIds] = useState([]);
  const [newStudentData, setNewStudentData] = useState({ first_name: '', family_name: '', email: '' });
  const [error, setError] = useState(null);
  const [isNameEditModalOpen, setIsNameEditModalOpen] = useState(false);
  const [isAddClassModalOpen, setIsAddClassModalOpen] = useState(false);
  const [selectedScheduleItem, setSelectedScheduleItem] = useState(null);
  const [isBatchImportModalOpen, setIsBatchImportModalOpen] = useState(false);
  const [batchImportText, setBatchImportText] = useState('');
  const [batchImportErrors, setBatchImportErrors] = useState([]);
  const [isBatchImporting, setIsBatchImporting] = useState(false);
  const [updatingEmailPrefs, setUpdatingEmailPrefs] = useState(new Set()); // Track which student IDs are being updated
  const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'success' }); // 'success' or 'error'

  // Schedule overlay state
  const [showScheduleOverlay, setShowScheduleOverlay] = useState(false);
  const [overlayPeriod, setOverlayPeriod] = useState('all'); // 'all' or period ID - shared with associated classes
  const [scheduleOverlayData, setScheduleOverlayData] = useState([]);
  const [availablePeriods, setAvailablePeriods] = useState([]);

  const prevGroupIdRef = useRef(currentUrlGroupId); // Initialize ref with current ID

  console.log(`[GroupDetailPage] Initializing/Rendering. URL_ID: ${currentUrlGroupId}, isLoading: ${isLoading}, internalData_ID: ${internalGroupData?.id} (from initial state), Prop_ID: ${group?.id}, Prop_Valid_Initial: ${isGroupPropInitiallyValid}`);

  // Snackbar helper function
  const showSnackbar = (message, type = 'success') => {
    setSnackbar({ show: true, message, type });
    setTimeout(() => {
      setSnackbar({ show: false, message: '', type: 'success' });
    }, 4000); // Hide after 4 seconds
  };

  // Main useEffect: Handle group data fetching and setup
  useEffect(() => {
    const currentUrlGroupId = parseInt(groupIdFromParams);
    const groupIdHasChanged = prevGroupIdRef.current !== currentUrlGroupId;

    console.log('[GroupDetailPage useEffect] URL Group ID:', currentUrlGroupId, 'Changed:', groupIdHasChanged);

    if (groupIdHasChanged) {
      console.log(`[GroupDetailPage useEffect] Group ID changed to ${currentUrlGroupId}. Clearing states.`);
      setInternalGroupData(null);
      setAssociatedClasses([]);
      setError(null);
      setIsLoading(false);
    }

    // Check if the current `group` prop is valid for the `currentUrlGroupId`
    const isGroupPropNowValid = group && group.id === currentUrlGroupId && group.students && group.preferences;

    if (isGroupPropNowValid) {
      console.log('[GroupDetailPage useEffect] Using valid group prop. Name:', group.name);
      setInternalGroupData(group);
      // Fetch schedule entries for this group
      fetchScheduleEntriesForGroup(currentUrlGroupId, overlayPeriod);
      setIsLoading(false);
      setError(null);
    }
    // If prop is not valid, but we *can* fetch for the currentUrlGroupId
    else if (user?.token) {
      const shouldFetch = groupIdHasChanged || !internalGroupData || internalGroupData.id !== currentUrlGroupId;

      if (shouldFetch) {
        console.log(`[GroupDetailPage useEffect] Conditions met for fetching for ID ${currentUrlGroupId}. Will set isLoading=true.`);
        setIsLoading(true);
        setError(null);

        const fetchGroupDetails = async () => {
          console.log('[GroupDetailPage fetchGroupDetails] Starting fetch for ID:', currentUrlGroupId);
          try {
            if (currentUrlGroupId === null) throw new Error("Cannot fetch without a valid group ID.");
            const fetchedGroup = await getGroupById(String(currentUrlGroupId), user.token, 'students,preferences');
            console.log('[GroupDetailPage fetchGroupDetails] Fetched group data:', fetchedGroup);

            if (parseInt(groupIdFromParams) === currentUrlGroupId) {
              setInternalGroupData(fetchedGroup);
              // Fetch schedule entries for this group
              await fetchScheduleEntriesForGroup(currentUrlGroupId, overlayPeriod);
              setError(null);
              setIsLoading(false);
            } else {
              console.log(`[GroupDetailPage fetchGroupDetails] URL ID changed during fetch. Discarding.`);
            }
          } catch (err) {
            console.error('[GroupDetailPage fetchGroupDetails] Error fetching group:', err);
            if (parseInt(groupIdFromParams) === currentUrlGroupId) {
              setError(err.message || 'Failed to fetch group details.');
              setInternalGroupData(null);
              setIsLoading(false);
            }
          }
        };
        fetchGroupDetails();
      } else if (!isLoading && (!internalGroupData || internalGroupData.id !== currentUrlGroupId)) {
        console.log(`[GroupDetailPage useEffect] Prop not valid, not fetching, no valid internal data for ${currentUrlGroupId}. Setting error or relying on render logic.`);
        if (!error) setError(`Data for group ${currentUrlGroupId} is currently unavailable.`);
        setIsLoading(false);
      }
    }
    // Cannot use prop, cannot fetch (no token)
    else if (currentUrlGroupId) {
      console.log(`[GroupDetailPage useEffect] Cannot fetch (no token) for ID ${currentUrlGroupId}.`);
      setError("Authentication is required to load group details.");
      setInternalGroupData(null);
      setIsLoading(false);
    }

    prevGroupIdRef.current = currentUrlGroupId;
  }, [groupIdFromParams, group, user?.token, currentUrlGroupId]);

  // Fetch available periods from academic year structure
  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        const academicYearService = AcademicYearService.getInstance();
        const yearStructure = await academicYearService.getAcademicYearStructure();

        if (yearStructure && yearStructure.periods) {
          setAvailablePeriods(yearStructure.periods);
        }
      } catch (error) {
        console.error('Error fetching periods:', error);
        setAvailablePeriods([]);
      }
    };

    fetchPeriods();
  }, []);

  // Helper function to check if week series overlaps with a period
  const weekSeriesOverlapsPeriod = useCallback((weekSeries, periodStartWeek, periodEndWeek) => {
    if (!weekSeries) return false;

    // Parse week series to get individual week numbers
    const weekNumbers = new Set();
    const parts = weekSeries.split(',');

    for (const part of parts) {
      const trimmedPart = part.trim();
      if (trimmedPart.includes('-')) {
        const [startStr, endStr] = trimmedPart.split('-');
        const start = parseInt(startStr, 10);
        const end = parseInt(endStr, 10);

        if (!isNaN(start) && !isNaN(end)) {
          if (start <= end) {
            // Normal range
            for (let i = start; i <= end; i++) {
              weekNumbers.add(i);
            }
          } else {
            // Cross-year range like "52-4"
            for (let i = start; i <= 53; i++) {
              weekNumbers.add(i);
            }
            for (let i = 1; i <= end; i++) {
              weekNumbers.add(i);
            }
          }
        }
      } else {
        const weekNum = parseInt(trimmedPart, 10);
        if (!isNaN(weekNum)) {
          weekNumbers.add(weekNum);
        }
      }
    }

    // Check if any week in the series falls within the period
    for (const week of weekNumbers) {
      if (periodStartWeek <= periodEndWeek) {
        // Normal period
        if (week >= periodStartWeek && week <= periodEndWeek) {
          return true;
        }
      } else {
        // Cross-year period
        if (week >= periodStartWeek || week <= periodEndWeek) {
          return true;
        }
      }
    }

    return false;
  }, []);

  // Function to fetch schedule entries for a specific group (with optional period filter)
  const fetchScheduleEntriesForGroup = useCallback(async (groupId, periodFilter = 'all') => {
    try {
      console.log('[GroupDetailPage] Fetching schedule entries for group:', groupId);
      const scheduleEntries = await getScheduleEntries();
      console.log('[GroupDetailPage] All schedule entries:', scheduleEntries);

      // Find selected period if one is selected
      const selectedPeriod = periodFilter !== 'all'
        ? availablePeriods.find(p => p.id?.toString() === periodFilter.toString())
        : null;

      // Filter entries that include this group in their groups array
      const groupScheduleEntries = scheduleEntries.filter(entry => {
        // Check if this group is associated with the schedule entry
        const groupMatch = entry.groups && entry.groups.some(entryGroup => entryGroup.id === groupId);

        if (!groupMatch) return false;

        // Filter by period if a specific period is selected
        if (selectedPeriod) {
          const weekSeries = entry.week_series || entry.weekSeries;
          if (!weekSeries) return false;

          // Check if entry's week series overlaps with the selected period
          return weekSeriesOverlapsPeriod(
            weekSeries,
            selectedPeriod.startWeek || selectedPeriod.start_week,
            selectedPeriod.endWeek || selectedPeriod.end_week
          );
        }

        return true;
      });

      console.log('[GroupDetailPage] Filtered schedule entries for group:', groupScheduleEntries);

      // Transform the entries to match the expected format for display
      const transformedEntries = groupScheduleEntries.map(entry => ({
        id: entry.id,
        course: entry.subject_name,
        teacher: entry.teacher_name,
        room: entry.room_name,
        day: entry.day,
        time: entry.start_time && entry.end_time
          ? `${entry.start_time} - ${entry.end_time}`
          : 'Unscheduled',
        type: entry.entry_type
      }));

      setAssociatedClasses(transformedEntries);
    } catch (error) {
      console.error('[GroupDetailPage] Error fetching schedule entries:', error);
      // Don't show error for schedule entries, just leave the list empty
      setAssociatedClasses([]);
    }
  }, [availablePeriods, weekSeriesOverlapsPeriod]);

  // Refetch associated classes when period filter changes
  useEffect(() => {
    if (internalGroupData?.id) {
      fetchScheduleEntriesForGroup(internalGroupData.id, overlayPeriod);
    }
  }, [overlayPeriod, internalGroupData?.id, fetchScheduleEntriesForGroup]);

  // Fetch schedule overlay data when overlay is enabled
  useEffect(() => {
    const fetchScheduleOverlay = async () => {
      if (!showScheduleOverlay || !internalGroupData) {
        setScheduleOverlayData([]);
        return;
      }

      try {
        // Fetch all schedule entries
        const allEntries = await getScheduleEntries();

        // Find selected period if one is selected
        const selectedPeriod = overlayPeriod !== 'all'
          ? availablePeriods.find(p => p.id?.toString() === overlayPeriod.toString())
          : null;

        // Filter entries for this group
        const groupEntries = allEntries.filter(entry => {
          // Check if group matches
          const groupMatch = entry.groups && entry.groups.some(entryGroup =>
            entryGroup.id === internalGroupData.id || entryGroup === internalGroupData.id
          );

          if (!groupMatch) return false;

          // Filter by period if a specific period is selected
          if (selectedPeriod) {
            const weekSeries = entry.week_series || entry.weekSeries;
            if (!weekSeries) return false;

            // Check if entry's week series overlaps with the selected period
            return weekSeriesOverlapsPeriod(
              weekSeries,
              selectedPeriod.startWeek || selectedPeriod.start_week,
              selectedPeriod.endWeek || selectedPeriod.end_week
            );
          }

          return true;
        });

        setScheduleOverlayData(groupEntries);
      } catch (error) {
        console.error('Error fetching schedule overlay:', error);
        setScheduleOverlayData([]);
      }
    };

    fetchScheduleOverlay();
  }, [showScheduleOverlay, internalGroupData, overlayPeriod, availablePeriods, weekSeriesOverlapsPeriod]);

  // Effect to update editedName when internalGroupData (for the correct group) changes
  useEffect(() => {
    if (internalGroupData && internalGroupData.id === currentUrlGroupId) {
      setEditedName(internalGroupData.name || '');
      console.log(`[GroupDetailPage] Syncing editedName to: "${internalGroupData.name || ''}" from internalGroupData for ID ${currentUrlGroupId}.`);
    } else {
      // If internalGroupData is null or for a different group, clear editedName
      // This also handles the case where groupIdHasChanged in the main effect and internalGroupData becomes null
      setEditedName('');
      console.log(`[GroupDetailPage] Clearing or not setting editedName because internalGroupData (ID: ${internalGroupData?.id}) doesn't match current URL ID ${currentUrlGroupId}.`);
    }

    // Clear any lingering email preference update states when group data changes
    setUpdatingEmailPrefs(new Set());
  }, [internalGroupData, currentUrlGroupId]);

  // Cleanup effect to reset loading states when component unmounts or URL changes
  useEffect(() => {
    return () => {
      // Clear loading states when component unmounts
      setUpdatingEmailPrefs(new Set());
    };
  }, [currentUrlGroupId]); // Reset when URL group ID changes

  // --- Add Class Modal Handlers ---
  const openAddClassModal = () => {
    if (!internalGroupData || internalGroupData.id === undefined) return;

    // Create a template schedule item for adding a new class
    const currentWeek = getWeekNumber(new Date());
    const templateItem = new ScheduleEntry({
      id: `temp-add-${Date.now()}`,
      day: 'monday', // Default to Monday
      timeStart: '',
      timeEnd: '',
      course: '',
      subject: '',
      teacher: '',
      groups: [internalGroupData.id], // Pre-select the current group
      room: '',
      type: 'class',
      weekSeries: currentWeek.toString(),
      year: 2025 // Force year to 2025
    });

    setSelectedScheduleItem(templateItem);
    setIsAddClassModalOpen(true);
  };

  const closeAddClassModal = () => {
    setIsAddClassModalOpen(false);
    setSelectedScheduleItem(null);
  };

  const handleSaveNewClass = async (editedDataObj, originalItem) => {
    if (typeof onAddNewClass === 'function' && internalGroupData?.id !== undefined) {
      try {
        // Convert the edited data to the format expected by onAddNewClass
        const newClassDetails = {
          ...editedDataObj,
          // Map frontend field names to backend expected names
          subject_name: editedDataObj.subject || editedDataObj.course,
          teacher_name: editedDataObj.teacher,
          room_name: editedDataObj.room,
          entry_type: 'class',
          start_time: editedDataObj.timeStart,
          end_time: editedDataObj.timeEnd
        };

        await onAddNewClass(newClassDetails);
        // Refresh associated classes list after adding
        await fetchScheduleEntriesForGroup(internalGroupData.id, overlayPeriod);
        console.log('[GroupDetailPage] Refreshed associated classes after adding new class');
      } catch (error) {
        console.error('[GroupDetailPage] Error adding new class:', error);
        alert("Error: Could not save the new class.");
      }
    } else {
      console.warn("onAddNewClass function not provided or group ID missing.");
      alert("Error: Could not save the new class.");
    }
    closeAddClassModal();
  };

  // --- Edit Handlers (Students) ---
  const handleNewStudentInputChange = (e) => {
    const { name, value } = e.target;
    setNewStudentData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddStudentSubmit = (e) => {
    e.preventDefault();
    const firstName = newStudentData.first_name.trim();
    const familyName = newStudentData.family_name.trim();
    const studentEmail = newStudentData.email.trim();

    if (!firstName || !familyName || !studentEmail) {
      alert('Student first name, family name, and email are required.');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(studentEmail)) {
      alert('Please enter a valid email address.');
      return;
    }

    if (typeof onAddStudentToGroup === 'function' && internalGroupData?.id) {
      onAddStudentToGroup(internalGroupData.id, {
        first_name: firstName,
        family_name: familyName,
        email: studentEmail,
        wants_emails: true // Default to true for new students
      });
      setNewStudentData({ first_name: '', family_name: '', email: '' }); // Clear form after submission attempt
    } else {
      console.warn("onAddStudentToGroup handler not provided or group ID missing.");
      alert("Error adding student.");
    }
  };

  // --- Name Edit Modal Handlers ---
  const openNameEditModal = () => {
    if (internalGroupData) {
      setEditedName(internalGroupData.name);
      setIsNameEditModalOpen(true);
    }
  };
  const closeNameEditModal = () => setIsNameEditModalOpen(false);
  const handleSaveName = (e) => {
    e.preventDefault();
    const trimmedName = editedName.trim();
    if (!trimmedName) {
      alert("Group name cannot be empty.");
      return;
    }
    if (typeof onUpdateGroup === 'function' && internalGroupData) {
      // Send only the changed name and potentially group_code if editable
      onUpdateGroup({ ...internalGroupData, name: trimmedName });
      closeNameEditModal();
    } else {
      console.warn("onUpdateGroup handler not provided or groupData missing.");
    }
  };

  // --- Batch Import Modal Handlers ---
  const openBatchImportModal = () => {
    setBatchImportText('');
    setBatchImportErrors([]);
    setIsBatchImportModalOpen(true);
  };
  const closeBatchImportModal = () => {
    setIsBatchImportModalOpen(false);
    setBatchImportText('');
    setBatchImportErrors([]);
  };

  const handleBatchImportSubmit = async (e) => {
    e.preventDefault();
    if (!batchImportText.trim()) {
      alert("Please enter student data to import.");
      return;
    }

    setIsBatchImporting(true);
    setBatchImportErrors([]);

    try {
      // Parse the input text
      const lines = batchImportText.trim().split('\n').filter(line => line.trim());
      const studentsToImport = [];
      const errors = [];

      lines.forEach((line, index) => {
        const lineNumber = index + 1;
        const parts = line.split(',').map(part => part.trim());

        if (parts.length !== 3) {
          errors.push(`Line ${lineNumber}: Expected 3 fields (First Name, Family Name, Email) but found ${parts.length}`);
          return;
        }

        const [firstName, familyName, email] = parts;

        if (!firstName) {
          errors.push(`Line ${lineNumber}: First name is required`);
        }
        if (!familyName) {
          errors.push(`Line ${lineNumber}: Family name is required`);
        }
        if (!email) {
          errors.push(`Line ${lineNumber}: Email is required`);
        } else if (!/\S+@\S+\.\S+/.test(email)) {
          errors.push(`Line ${lineNumber}: Invalid email format: ${email}`);
        }

        if (firstName && familyName && email && /\S+@\S+\.\S+/.test(email)) {
          studentsToImport.push({
            first_name: firstName,
            family_name: familyName,
            email: email.toLowerCase(),
            wants_emails: true // Default to true for new students
          });
        }
      });

      if (errors.length > 0) {
        setBatchImportErrors(errors);
        setIsBatchImporting(false);
        return;
      }

      // Use the batch API to process all students at once
      if (typeof onAddStudentsToGroupBatch === 'function' && internalGroupData?.id) {
        const result = await onAddStudentsToGroupBatch(internalGroupData.id, studentsToImport);

        const { successful, failed } = result.results;

        // Show detailed results
        if (successful.length > 0) {
          const message = `Successfully imported ${successful.length} student(s).${failed.length > 0 ? ` ${failed.length} failed.` : ''}`;
          alert(message);
        }

        if (failed.length > 0) {
          const failedErrors = failed.map(f => `Line ${f.line}: ${f.error}`);
          setBatchImportErrors(failedErrors);
        } else {
          closeBatchImportModal();
        }
      } else {
        throw new Error("Batch import handler not provided or group ID missing.");
      }

    } catch (error) {
      console.error('Batch import error:', error);
      setBatchImportErrors([`Unexpected error: ${error.message}`]);
    } finally {
      setIsBatchImporting(false);
    }
  };

  // --- Preference Update Handlers ---
  const updateLocalGroupPreferences = useCallback((newPreferences) => {
    setInternalGroupData(prev => prev ? { ...prev, preferences: newPreferences } : null);
  }, []);

  const loadGroupTagSuggestions = useCallback(async () => {
    const allTags = (groups || [])
      .flatMap(g => Array.isArray(g.tags) ? g.tags : [])
      .map(tag => String(tag).trim())
      .filter(Boolean);
    const uniqueTags = Array.from(new Set(allTags.map(tag => tag.toLowerCase())))
      .map(lowerTag => allTags.find(tag => tag.toLowerCase() === lowerTag))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    return uniqueTags;
  }, [groups]);

  const persistGroupTags = useCallback(async (tagsToSave) => {
    const updated = await onUpdateGroup({
      ...internalGroupData,
      tags: tagsToSave
    });
    if (updated) setInternalGroupData(updated);
  }, [internalGroupData, onUpdateGroup]);

  // Remove handler remains the same (uses studentId)
  const handleRemoveStudentFromGroup = (studentIdToRemove) => {
    if (typeof onRemoveStudentFromGroup === 'function' && internalGroupData?.id) {
      onRemoveStudentFromGroup(internalGroupData.id, studentIdToRemove);
    } else {
      console.warn("onRemoveStudentFromGroup handler not provided or group ID missing.");
      alert("Error removing student.");
    }
  };

  // Email preference toggle handler
  const handleToggleStudentEmailPreference = async (studentId, currentWantsEmails) => {
    if (!studentId) {
      console.warn("Student ID missing for email preference toggle.");
      return;
    }

    // Prevent multiple simultaneous requests for the same student
    if (updatingEmailPrefs.has(studentId)) {
      console.log(`Email preference update already in progress for student ${studentId}`);
      return;
    }

    // Add to loading set
    setUpdatingEmailPrefs(prev => new Set(prev).add(studentId));

    // Safety timeout to prevent stuck loading states
    const timeoutId = setTimeout(() => {
      console.warn(`Email preference update timed out for student ${studentId}, clearing loading state`);
      setUpdatingEmailPrefs(prev => {
        const newSet = new Set(prev);
        newSet.delete(studentId);
        return newSet;
      });
    }, 10000); // 10 second timeout

    try {
      console.log(`Toggling email preference for student ${studentId} from ${currentWantsEmails} to ${!currentWantsEmails}`);

      const updatedStudent = await updateStudentEmailPreference(studentId, !currentWantsEmails);

      console.log(`Email preference API response:`, updatedStudent);

      // Update the local state to reflect the change
      setInternalGroupData(prev => {
        if (!prev || !prev.students) return prev;

        return {
          ...prev,
          students: prev.students.map(student =>
            student.id === studentId
              ? { ...student, wants_emails: updatedStudent.wants_emails }
              : student
          )
        };
      });

      console.log(`Email preference updated for student ${studentId}: ${updatedStudent.wants_emails}`);

      // Show success message
      const action = updatedStudent.wants_emails ? 'subscribed to' : 'unsubscribed from';
      const studentName = internalGroupData?.students?.find(s => s.id === studentId);
      const name = studentName ? `${studentName.first_name} ${studentName.family_name}` : 'Student';
      showSnackbar(`${name} successfully ${action} email notifications.`, 'success');

    } catch (error) {
      console.error("Error updating student email preference:", error);
      showSnackbar(`Failed to update email preference: ${error.message}`, 'error');
    } finally {
      // Clear the timeout
      clearTimeout(timeoutId);

      // Remove from loading set
      setUpdatingEmailPrefs(prev => {
        const newSet = new Set(prev);
        newSet.delete(studentId);
        return newSet;
      });
    }
  };

  // --- Save/Delete/Navigation Handlers ---
  const handleSaveChanges = async () => {
    if (!internalGroupData) return;
    console.log("Saving changes for group:", internalGroupData.id);
    if (typeof onUpdateGroup === 'function') {
      try {
        // Save changes
        await onUpdateGroup(internalGroupData);
        console.log('[GroupDetailPage] Group updated successfully');

        // Always refresh from server to ensure we have the correct data
        if (user?.token) {
          console.log('[GroupDetailPage] Refreshing group data from server after save...');
          const refreshedGroup = await getGroupById(String(internalGroupData.id), user.token, 'students,preferences');
          setInternalGroupData(refreshedGroup);
          // Also refresh associated classes
          await fetchScheduleEntriesForGroup(internalGroupData.id, overlayPeriod);
          showSnackbar('Changes saved successfully!', 'success');
        } else {
          showSnackbar('Changes saved successfully!', 'success');
        }
      } catch (error) {
        console.error('[GroupDetailPage] Error saving changes:', error);
        showSnackbar(`Error saving changes: ${error.message}`, 'error');
      }
    } else {
      console.warn("onUpdateGroup function not provided.");
      showSnackbar('Error: Save function not available', 'error');
    }
  };

  const handleDeleteClick = () => {
    if (!internalGroupData) return;
    if (window.confirm(`Are you sure you want to delete group "${internalGroupData.name}"? This action cannot be undone.`)) {
      console.log("Deleting group:", internalGroupData.id);
      if (typeof onDeleteGroup === 'function') {
        onDeleteGroup(internalGroupData.id);
      } else {
        console.warn("onDeleteGroup function not provided.");
        navigate('/setup/groups');
      }
    }
  }
  const handleGoBack = () => {
    navigate('/setup/groups');
  }

  // --- Render Logic ---

  // 1. Most critical: No Group ID in URL
  if (currentUrlGroupId === null) {
    console.log("[GroupDetailPage] Render: No currentUrlGroupId. Displaying error.");
    return <div className="p-6 text-center text-red-500">Error: No Group ID specified in the URL.</div>;
  }

  // 2. We are actively loading AND we have NO data (neither fresh nor stale) for the current group ID.
  if (isLoading && (!internalGroupData || internalGroupData.id !== currentUrlGroupId)) {
    console.log(`[GroupDetailPage] Render: Full Page Loading (isLoading: ${isLoading}, no valid internal data for ID ${currentUrlGroupId})`);
    return <div className="p-6 text-center">Loading group details for ID {currentUrlGroupId}...</div>;
  }

  // 3. An error occurred.
  if (error) {
    console.log(`[GroupDetailPage] Render: Error Display (${error}). Stale data ID (if any): ${internalGroupData?.id}`);
    return (
      <div className="p-6 text-center text-red-600">
        <p>Error loading group ID {currentUrlGroupId}: {error}</p>
        {internalGroupData && internalGroupData.id === currentUrlGroupId && (
          <p className="text-sm text-gray-700 mt-2">(Displaying last known data, refresh may have failed)</p>
        )}
        <Button variant="secondary" onClick={handleGoBack} className="mt-4">Back to Group List</Button>
      </div>
    );
  }

  // 4. Not loading, no error, but still no valid data for the current group ID.
  if (!internalGroupData || internalGroupData.id !== currentUrlGroupId) {
    console.log(`[GroupDetailPage] Render: Not Found (No/mismatched internal data for ID ${currentUrlGroupId}, not loading, no error)`);
    return (
      <div className="p-6 text-center text-gray-500">
        <p>Group data for ID {currentUrlGroupId} could not be found or is currently unavailable.</p>
        <Button variant="secondary" onClick={handleGoBack} className="mt-4">Back to Group List</Button>
      </div>
    );
  }

  // 5. We have valid internalGroupData. Render the page.
  console.log(`[GroupDetailPage] Render: Displaying group ID ${internalGroupData.id}. isLoading (for background refresh): ${isLoading}`);
  return (
    <div className="space-y-6">
      {isLoading && (
        <div className="fixed top-16 left-1/2 transform -translate-x-1/2 bg-blue-100 text-blue-700 px-4 py-2 rounded-b-md shadow-lg text-sm z-50">
          Refreshing group data...
        </div>
      )}
      {/* Top Bar */}
      <div className="relative flex justify-between items-center mb-4 min-h-[40px]">
        <Button variant="secondary" onClick={handleGoBack}>
          &larr; Back to Group List
        </Button>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center gap-2">
          <h2 className="text-2xl font-bold text-gray-800 text-center whitespace-nowrap">
            {internalGroupData.name}

          </h2>
          <Button
            variant="secondary"
            onClick={openNameEditModal}
            className="p-1 text-gray-500 hover:text-gray-700 flex-shrink-0"
            title="Edit group name"
          >
            <PencilIcon />
          </Button>
        </div>
        <div></div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <div className="p-4 flex flex-col h-full">
            <div className="flex justify-between items-center mb-2 border-b pb-2">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-700">Students ({internalGroupData.students?.length || 0})</h3>
                <HelpIcon
                  documentationId="group-students"
                  size={16}
                  variant="info"
                  position="inline"
                />
              </div>
              <Button
                variant="secondary"
                onClick={openBatchImportModal}
                className="text-sm py-1 px-2 flex items-center space-x-1"
                title="Import Students in Batch"
              >
                <ImportIcon />
                <span>Import</span>
              </Button>
            </div>
            <form onSubmit={handleAddStudentSubmit} className="flex items-end space-x-2 mb-3">
              <div className="flex-1">
                <label htmlFor="new-student-first-name" className="block text-xs font-medium text-gray-700 mb-1">First Name</label>
                <Input id="new-student-first-name" name="first_name" value={newStudentData.first_name} onChange={handleNewStudentInputChange} placeholder="First Name" required className="text-sm py-1" />
              </div>
              <div className="flex-1">
                <label htmlFor="new-student-family-name" className="block text-xs font-medium text-gray-700 mb-1">Family Name</label>
                <Input id="new-student-family-name" name="family_name" value={newStudentData.family_name} onChange={handleNewStudentInputChange} placeholder="Family Name" required className="text-sm py-1" />
              </div>
              <div className="flex-1">
                <label htmlFor="new-student-email" className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                <Input id="new-student-email" name="email" type="email" value={newStudentData.email} onChange={handleNewStudentInputChange} placeholder="email@example.com" required className="text-sm py-1" />
              </div>
              <Button type="submit" variant="primary" className="text-sm py-1 px-3 self-end h-[34px] flex-shrink-0">Add Student</Button>
            </form>
            <p className="text-xs text-gray-500 mb-3">Adds an existing student by email or creates a new one if not found.</p>
            <div className="flex-1 min-h-0">
              {/* Header row for columns */}
              <div className="flex justify-between items-center text-xs font-medium text-gray-600 mb-1 px-2">
                <span>Student</span>
                <div className="flex items-center space-x-16">
                  <span>Email Notifications</span>
                  <span>Actions</span>
                </div>
              </div>
              <ul className="h-full overflow-y-auto divide-y divide-gray-200 border rounded-md p-2 bg-gray-50/50">
                {(internalGroupData.students?.length || 0) > 0 ? (
                  internalGroupData.students.map(student => (
                    <li key={student.id} className="py-1.5 flex justify-between items-center text-sm">
                      <div className="flex-1">
                        <div className="flex items-center">
                          <span className="font-medium">{`${student.first_name} ${student.family_name}`}</span>
                          <span className="text-gray-500 ml-2">({student.email})</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-12 flex-shrink-0 ml-4">
                        <div className="flex items-center">
                          {student.wants_emails ? (
                            <div className="flex items-center text-green-600">
                              <MailIcon />
                              <span className="ml-1 text-xs font-medium">Yes</span>
                            </div>
                          ) : (
                            <div className="flex items-center text-red-600">
                              <MailSlashIcon />
                              <span className="ml-1 text-xs font-medium">No</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-1">
                          <Button
                            variant={student.wants_emails ? "danger" : "primary"}
                            className="text-xs py-0.5 px-1.5"
                            onClick={() => handleToggleStudentEmailPreference(student.id, student.wants_emails)}
                            disabled={updatingEmailPrefs.has(student.id)}
                            title={student.wants_emails ? "Unsubscribe from email notifications" : "Subscribe to email notifications"}
                          >
                            {updatingEmailPrefs.has(student.id) ? (
                              <div className="animate-spin h-3 w-3 border border-current border-t-transparent rounded-full"></div>
                            ) : (
                              student.wants_emails ? <MailSlashIcon /> : <MailIcon />
                            )}
                          </Button>
                          <Button
                            variant="danger"
                            className="text-xs py-0.5 px-1.5"
                            onClick={() => handleRemoveStudentFromGroup(student.id)}
                            title={`Remove ${student.first_name} ${student.family_name}`}
                          >
                            <TrashIcon />
                          </Button>
                        </div>
                      </div>
                    </li>
                  ))
                ) : (<li className="text-center text-gray-500 py-2 italic">No students in this group yet.</li>)}
              </ul>
            </div>
          </div>
        </Card>

        <div className="space-y-6">
          <Card>
            <div className="p-4">
              <div className="flex items-center justify-between mb-2 border-b pb-2">
                <h3 className="text-lg font-semibold text-gray-700">Time Preferences</h3>
                <div className="flex items-center gap-2">
                  {showScheduleOverlay && (
                    <select
                      value={overlayPeriod}
                      onChange={(e) => setOverlayPeriod(e.target.value)}
                      className="text-xs border border-gray-300 rounded px-2 py-1"
                    >
                      <option value="all">All Periods</option>
                      {availablePeriods.map(period => (
                        <option key={period.id} value={period.id}>
                          {period.name}
                        </option>
                      ))}
                    </select>
                  )}
                  <div
                    onClick={() => setShowScheduleOverlay(!showScheduleOverlay)}
                    className={`cursor-pointer transition-colors ${showScheduleOverlay
                      ? 'text-blue-600 hover:text-blue-700'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                    title={showScheduleOverlay ? "Hide scheduled courses" : "Show scheduled courses"}
                  >
                    <CalendarIcon />
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-1">Click or drag grid cells to edit group time preferences.</p>
              <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mb-3 text-xs text-gray-600">
                <span className="font-semibold">Legend:</span>
                <div className="flex items-center"><span className="w-3 h-3 rounded-sm bg-green-200 mr-1 inline-block border border-gray-300"></span><span>Free</span></div>
                <div className="flex items-center"><span className="w-3 h-3 rounded-sm bg-orange-200 mr-1 inline-block border border-gray-300"></span><span>Prefer Not</span></div>
                <div className="flex items-center"><span className="w-3 h-3 rounded-sm bg-red-200 mr-1 inline-block border border-gray-300"></span><span>Cannot</span></div>
                {showScheduleOverlay && (
                  <div className="flex items-center"><span className="w-3 h-3 rounded-sm bg-blue-500 bg-opacity-30 border-2 border-blue-500 mr-1 inline-block"></span><span>Scheduled</span></div>
                )}
              </div>
              <TeacherPreferenceGrid
                preferences={internalGroupData.preferences || []}
                onPreferencesChange={updateLocalGroupPreferences}
                overlaySchedule={showScheduleOverlay ? scheduleOverlayData : []}
                onScheduleHover={setHoveredCourseIds}
              />
            </div>
          </Card>

          <AssociatedClassesCard
            classes={associatedClasses}
            filterPeriod={overlayPeriod}
            onFilterChange={setOverlayPeriod}
            availablePeriods={availablePeriods}
            onAddClass={openAddClassModal}
            emptyMessage="No classes scheduled for this group"
            entityType="group"
            documentationId="group-associated-classes"
            hoveredCourseIds={hoveredCourseIds}
          />

          <EntityTagsSection
            tags={internalGroupData.tags}
            onPersistTags={persistGroupTags}
            loadTagSuggestions={loadGroupTagSuggestions}
            documentationId="group-tags"
            modalTitle="Edit Group Tags"
            persistErrorMessage="Failed to update group tags. Please try again."
          />
        </div>
      </div>

      <div className="mt-6 flex justify-between items-center border-t pt-4">
        <Button variant="danger" onClick={handleDeleteClick} className="flex items-center space-x-1">
          <TrashIcon /> <span>Delete Group</span>
        </Button>
        <div className="flex space-x-3">
          <Button variant="secondary" onClick={handleGoBack}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveChanges}>Save Changes</Button>
        </div>
      </div>

      <Modal isOpen={isNameEditModalOpen} onClose={closeNameEditModal} title="Edit Group Name">
        <form onSubmit={handleSaveName}>
          <div className="mb-4">
            <label htmlFor="edit-group-name-modal" className="block text-sm font-medium text-gray-700 mb-1">Group Name</label>
            <Input id="edit-group-name-modal" value={editedName} onChange={(e) => setEditedName(e.target.value)} placeholder="Enter group name" required autoFocus />
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <Button type="button" variant="secondary" onClick={closeNameEditModal}>Cancel</Button>
            <Button type="submit" variant="primary">Save Name</Button>
          </div>
        </form>
      </Modal>

      <EditScheduleItemModal
        isOpen={isAddClassModalOpen}
        onClose={closeAddClassModal}
        selectedScheduleItem={selectedScheduleItem}
        onSaveEdit={handleSaveNewClass}
        groups={groups}
        subjects={subjects}
        teachers={teachers}
        rooms={rooms}
        currentYear={2025}
        currentDate={new Date()}
        isAdmin={true}
        baseScheduleItems={[]}
        groupSpecificMode={internalGroupData && internalGroupData.id !== undefined ? {
          groupData: internalGroupData,
          directPublish: true,
          disableGroupRemoval: true
        } : undefined}
      />

      <Modal isOpen={isBatchImportModalOpen} onClose={closeBatchImportModal} title="Import Students in Batch">
        <form onSubmit={handleBatchImportSubmit} className="space-y-4">
          <div>
            <label htmlFor="batch-import-text" className="block text-sm font-medium text-gray-700 mb-2">
              Student Data
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Enter one student per line in the format: First Name, Family Name, email@example.com
            </p>
            <textarea
              id="batch-import-text"
              value={batchImportText}
              onChange={(e) => setBatchImportText(e.target.value)}
              placeholder="John, Doe, john.doe@example.com&#10;Jane, Smith, jane.smith@example.com&#10;..."
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-mono"
              disabled={isBatchImporting}
            />
          </div>

          {batchImportErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <h4 className="text-sm font-medium text-red-800 mb-2">Import Errors:</h4>
              <ul className="text-xs text-red-700 space-y-1">
                {batchImportErrors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 flex justify-end space-x-3">
            <Button
              type="button"
              variant="secondary"
              onClick={closeBatchImportModal}
              disabled={isBatchImporting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isBatchImporting}
            >
              {isBatchImporting ? 'Importing...' : 'Import Students'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Snackbar */}
      {snackbar.show && (
        <div className={`fixed bottom-4 right-4 z-50 max-w-sm w-full transform transition-all duration-300 ease-in-out ${snackbar.show ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
          }`}>
          <div className={`rounded-md p-4 shadow-lg ${snackbar.type === 'success'
            ? 'bg-green-50 border border-green-200'
            : 'bg-red-50 border border-red-200'
            }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {snackbar.type === 'success' ? (
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${snackbar.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}>
                  {snackbar.message}
                </p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setSnackbar({ show: false, message: '', type: 'success' })}
                  className={`inline-flex rounded-md p-1.5 ${snackbar.type === 'success'
                    ? 'text-green-500 hover:bg-green-100'
                    : 'text-red-500 hover:bg-red-100'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 ${snackbar.type === 'success' ? 'focus:ring-green-600' : 'focus:ring-red-600'
                    }`}
                >
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GroupDetailPage;
